import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {QuestionResponse1} from "../../../modules/admin/collect-review/models/question-response";

@Component({
    selector: 'app-question-preview',
    templateUrl: './question-preview.component.html'
})
export class QuestionPreviewComponent implements OnInit {
    @Input() question: QuestionResponse1;
    @Input() backgroundColor = '#FFFFFF';
    @Input() headerBackgroundColor = '#2563EB';
    @Input() headerTextColor = '#FFFFFF';
    @Input() redirectThreshold = 0;
    @Input() neutralThreshold: number | null = null;
    @Input() font = '';
    @Input() isValueSelected = false;
    @Input() questionLabelIndex?: number;
    @Output() sendResponseToParent: EventEmitter<string> = new EventEmitter();
    rating1_10 = Array.from({length: 10}, (_, i) => i + 1);
    iconList = [
        'mat_outline:mood_bad',
        'mat_outline:sentiment_dissatisfied',
        'mat_outline:sentiment_neutral',
        'mat_outline:sentiment_satisfied',
        'mat_outline:sentiment_very_satisfied'
    ]
    rating = 0;
    selectedChipIndexes = new Set<number>();

    ngOnInit() {
    }

    get questionNumber(): number {
        if (this.questionLabelIndex != null) {
            return this.questionLabelIndex;
        }
        if (this.question.displayOrder > 0) {
            return this.question.displayOrder - 1;
        }
        if (this.question.displayOrder < 0) {
            return this.question.displayOrder * -1;
        }
        return 1;
    }

    isChipSelected(index: number): boolean {
        return this.selectedChipIndexes.has(index);
    }

    chipStyle(index: number): Record<string, string> {
        const headerBg = this.headerBackgroundColor || '#2563EB';
        const headerText = this.headerTextColor || '#FFFFFF';
        const fontFamily = this.font ? `${this.font} !important` : undefined;
        if (this.isChipSelected(index)) {
            return {
                background: `linear-gradient(45deg, ${headerBg}, ${this.lightenHex(headerBg, 40)})`,
                color: headerText,
                'border-color': headerBg,
                ...(fontFamily ? {'font-family': fontFamily} : {})
            };
        }
        return {
            'background-color': '#FFFFFF',
            color: headerBg,
            'border-color': headerBg,
            ...(fontFamily ? {'font-family': fontFamily} : {})
        };
    }

    private lightenHex(hex: string, amount: number): string {
        const normalized = (hex || '').replace('#', '').trim();
        const full = normalized.length === 3
            ? normalized.split('').map(char => char + char).join('')
            : normalized;
        if (!/^[0-9a-fA-F]{6}$/.test(full)) {
            return hex || '#2563EB';
        }
        const num = parseInt(full, 16);
        const r = Math.min(255, Math.max(0, (num >> 16) + amount));
        const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amount));
        const b = Math.min(255, Math.max(0, (num & 0x0000FF) + amount));
        return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
    }

    toggleChip(index: number): void {
        if (this.selectedChipIndexes.has(index)) {
            this.selectedChipIndexes.delete(index);
        } else {
            this.selectedChipIndexes.add(index);
        }
        // Trigger change detection for Set mutations
        this.selectedChipIndexes = new Set(this.selectedChipIndexes);
    }

    isPrimaryNegativeRating(ratingIndex: number): boolean {
        return ratingIndex < this.redirectThreshold;
    }

    isPrimaryPositiveRating(ratingIndex: number): boolean {
        const neutralThreshold = this.neutralThreshold;
        if (neutralThreshold != null && neutralThreshold > this.redirectThreshold) {
            return ratingIndex >= neutralThreshold;
        }
        return ratingIndex >= this.redirectThreshold;
    }

    isPrimaryNeutralRating(ratingIndex: number): boolean {
        const neutralThreshold = this.neutralThreshold;
        return neutralThreshold != null
            && neutralThreshold > this.redirectThreshold
            && ratingIndex >= this.redirectThreshold
            && ratingIndex < neutralThreshold;
    }

    primaryRatingClass(ratingIndex: number): string {
        if (!this.isValueSelected || ratingIndex > this.rating) {
            return '';
        }
        if (!this.question.isPrimary) {
            return 'bg-orange-500 text-white';
        }
        if (this.isPrimaryPositiveRating(ratingIndex)) {
            return 'bg-green-500 text-white';
        }
        if (this.isPrimaryNeutralRating(ratingIndex)) {
            return 'bg-amber-500 text-white';
        }
        return 'bg-orange-500 text-white';
    }

    primarySmileyClass(ratingIndex: number): string {
        if (!this.isValueSelected || ratingIndex > this.rating) {
            return '';
        }
        if (!this.question.isPrimary) {
            return 'text-orange-500';
        }
        if (this.isPrimaryPositiveRating(ratingIndex)) {
            return 'text-green-500';
        }
        if (this.isPrimaryNeutralRating(ratingIndex)) {
            return 'text-amber-500';
        }
        return 'text-orange-500';
    }

    sendResponse(rating: number) {
        this.rating = rating;
        this.isValueSelected = true;
        if (rating < this.redirectThreshold) {
            this.sendResponseToParent.emit('negative');
        } else {
            const neutralThreshold = this.neutralThreshold;
            if (neutralThreshold != null && neutralThreshold > this.redirectThreshold && rating < neutralThreshold) {
                this.sendResponseToParent.emit('neutral');
            } else {
                this.sendResponseToParent.emit('positive');
            }
        }
    }
}
